// web/src/showcase.ts
// 「展示合集」页：把作者两个平台上的视频**以链接列表**的形式分类列出（不内嵌播放器）。
// 数据在 src/showcase-data.ts，改内容只动那个文件。
//
// 语言差异：
//   中文版 → 主链接是 B 站；有 YouTube 版的条目额外给一个 YouTube 入口。
//   英文版 → 只用 YouTube 链接与英文标题；没有 YouTube 版的条目直接不渲染，
//            也不再出现「作者的 B 站空间」卡片（英文站不出现任何 B 站链接）。
import { getLang, initI18n } from "./i18n";
import { BILIBILI_SPACE_URL, pickText, YOUTUBE_CHANNEL_URL } from "./site-data";
import { SHOWCASE_GROUPS } from "./showcase-data";

function escapeHtml(s: string): string {
  return s.replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
        c
      ] as string,
  );
}

/** 该条目在当前语言下可用的链接（英文版只有 urlEn，没有就不显示） */
function itemUrl(item: { url?: string; urlEn?: string }, isZh: boolean): string {
  return (isZh ? item.url : item.urlEn) || "";
}

function visibleGroups(isZh: boolean) {
  return SHOWCASE_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter((it) => Boolean(itemUrl(it, isZh))),
  })).filter((g) => g.items.length > 0);
}

function countVisible(isZh: boolean): number {
  return visibleGroups(isZh).reduce((n, g) => n + g.items.length, 0);
}

function renderGroups(): void {
  const lang = getLang();
  const isZh = lang === "zh";
  const root = document.getElementById("showcase-groups");
  if (!root) return;

  root.innerHTML = visibleGroups(isZh)
    .map((g) => {
      const items = g.items
        .map((item) => {
          const tags = (item.tags || [])
            .map(
              (t) => `<span class="chip">${escapeHtml(pickText(t, lang))}</span>`,
            )
            .join("");
          const note = item.note
            ? `<p class="video-link-note">${escapeHtml(pickText(item.note, lang))}</p>`
            : "";
          // 中文版：条目在 YouTube 上也有时，补一个 YouTube 入口（只外链）
          const alt =
            isZh && item.urlEn
              ? `<a class="video-link-alt" href="${escapeHtml(
                  item.urlEn,
                )}" target="_blank" rel="noopener noreferrer">YouTube ↗</a>`
              : "";
          return `<li class="video-link">
  <a class="video-link-title" href="${escapeHtml(itemUrl(item, isZh))}" target="_blank" rel="noopener noreferrer">${escapeHtml(
    pickText(item.title, lang),
  )}</a>
  ${note}
  <div class="video-link-meta">${tags}${alt}</div>
</li>`;
        })
        .join("\n");

      return `<section class="card" id="${escapeHtml(g.id)}">
  <h2>${escapeHtml(pickText(g.title, lang))}</h2>
  ${g.desc ? `<p class="sub">${escapeHtml(pickText(g.desc, lang))}</p>` : ""}
  <ul class="video-list">${items}</ul>
</section>`;
    })
    .join("\n");
}

/** 平台入口卡片：中文版给「B 站空间 + YouTube 频道」，英文版只给 YouTube */
function renderCards(): void {
  const isZh = getLang() === "zh";
  const root = document.getElementById("showcase-cards");
  if (!root) return;
  const ytCard = `<section class="card" id="showcase-yt-card">
  <h2>${isZh ? "作者的 YouTube 频道" : "Author's YouTube channel"}</h2>
  <p class="sub">${
    isZh
      ? "同一批内容在 YouTube 上也有（标题为英文）；英文版网站只使用 YouTube 链接。频道里还有不少 B 站没发过的内容。"
      : "The same videos with English titles, plus a few YouTube-only uploads. The English site uses YouTube links exclusively."
  }</p>
  <div class="feature-actions">
    <a class="btn secondary" href="${YOUTUBE_CHANNEL_URL}" target="_blank" rel="noopener noreferrer">${
      isZh ? "打开 YouTube 频道 →" : "Open the YouTube channel →"
    }</a>
  </div>
</section>`;
  const spaceCard = isZh
    ? `<section class="card" id="showcase-space-card">
  <h2>作者的 B 站空间</h2>
  <p class="sub">本页只挑了教学与代表作；更多演奏视频（合奏、单曲翻弹等）在作者的 B 站空间里。</p>
  <div class="feature-actions">
    <a class="btn secondary" href="${BILIBILI_SPACE_URL}" target="_blank" rel="noopener noreferrer">打开作者的 B 站空间 →</a>
  </div>
</section>`
    : "";
  root.innerHTML = spaceCard + ytCard;
}

function renderStaticText(): void {
  const isZh = getLang() === "zh";
  const set = (id: string, text: string) => {
    const el = document.getElementById(id);
    if (el) el.textContent = text;
  };
  const count = countVisible(isZh);

  document.title = isZh ? "展示合集 | MusicDoll" : "Showcase | MusicDoll";
  const meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (meta)
    meta.content = isZh
      ? "与 MusicDoll 相关的视频整理：教学、插件工作流与各乐器代表作的链接列表（只放链接，不内嵌播放器）。中文版链接到 Bilibili，英文版链接到 YouTube。"
      : "MusicDoll-related videos grouped by purpose — tutorials, plugin workflows and per-instrument highlights, all linking to the author's YouTube channel (links only, no embedded players).";
  set("showcase-title", isZh ? "展示合集" : "Showcase");
  set(
    "showcase-sub",
    isZh
      ? "与 MusicDoll 相关的视频整理：按用途分类，点标题直接到 B 站观看；在 YouTube 上也有同一批内容的条目，会额外给一个 YouTube 入口（本页只放链接，不内嵌播放器）。"
      : "MusicDoll-related videos grouped by purpose, all on the author's YouTube channel. Click a title to watch — this page links out only, no embedded players.",
  );
  set(
    "showcase-count",
    isZh ? `本页收录 ${count} 条` : `${count} videos on YouTube`,
  );
  set("showcase-platform-chip", isZh ? "Bilibili · YouTube" : "YouTube");
  set(
    "showcase-footer",
    isZh
      ? "想补充视频？把链接加进 src/showcase-data.ts 的分组里即可（url = B 站，urlEn = YouTube）。"
      : "Want to add a video? Append it to a group in src/showcase-data.ts (urlEn = YouTube link).",
  );
  renderCards();
}

function main(): void {
  initI18n();
  renderStaticText();
  renderGroups();
  document.addEventListener("langchange", () => {
    renderStaticText();
    renderGroups();
  });
}

main();
